import React, { useState, useEffect } from 'react';
import { Card, Title, Text, Badge, Flex } from '@tremor/react';
import { CurrencyDollarIcon, ArrowTrendingUpIcon, BanknotesIcon, UserMinusIcon } from '@heroicons/react/24/outline';
import { fetchStripeARR, fetchHSStripeReconcile } from '../services/api';

/**
 * RevenueKPIs — Top-of-page KPI cards from Stripe billing data.
 *
 * Shows: Total ARR, MRR, Lifetime Revenue Collected.
 * Below: collapsible HubSpot×Stripe reconciliation table (won deals × invoices).
 *
 * Server endpoints: /api/stripe-arr (10-min cache), /api/hs-stripe-reconcile (10-min cache).
 */
const RevenueKPIs = () => {
  const [arr, setArr] = useState(null);
  const [recon, setRecon] = useState(null);
  const [reconExpanded, setReconExpanded] = useState(false);
  const [churnExpanded, setChurnExpanded] = useState(false);
  const [loadingArr, setLoadingArr] = useState(true);
  const [loadingRecon, setLoadingRecon] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchStripeARR();
        if (!cancelled) setArr(data);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoadingArr(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lazy-load reconciliation when expanded
  useEffect(() => {
    if (reconExpanded && !recon && !loadingRecon) {
      setLoadingRecon(true);
      fetchHSStripeReconcile()
        .then(d => setRecon(d))
        .catch(e => setError(e.message || String(e)))
        .finally(() => setLoadingRecon(false));
    }
  }, [reconExpanded, recon, loadingRecon]);

  const fmt = (n) => `$${Math.round(n || 0).toLocaleString()}`;
  const fmtK = (n) => {
    const v = Math.round(n || 0);
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
    if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
    return `$${v}`;
  };

  return (
    <div className="mb-6 lg:mb-8">
      {/* HubSpot bookings (what we sold) + account-level churn */}
      <div className="mb-2">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">HubSpot Closed-Won (lifetime bookings)</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <KPICard
          label="HubSpot ARR"
          value={loadingArr ? '...' : fmtK(arr?.hs_won_arr)}
          subtitle={loadingArr ? '' : `${arr?.hs_won_count || 0} closed-won deals (lifetime)`}
          icon={ArrowTrendingUpIcon}
          color="slate"
        />
        <KPICard
          label="HubSpot MRR"
          value={loadingArr ? '...' : fmtK(arr?.hs_won_mrr)}
          subtitle="ARR / 12"
          icon={BanknotesIcon}
          color="slate"
        />
        <KPICard
          label="Churned Accounts"
          value={loadingArr ? '...' : (arr?.churned_account_count || 0)}
          subtitle={loadingArr ? '' : `${fmtK(arr?.churned_account_arr)} ARR lost · click for details`}
          icon={UserMinusIcon}
          color="purple"
          onClick={() => setChurnExpanded(!churnExpanded)}
          expanded={churnExpanded}
        />
      </div>

      {/* Expanded churn list */}
      {churnExpanded && arr?.churned_accounts?.length > 0 && (
        <Card className="mb-4 bg-purple-50 border border-purple-200">
          <Flex justifyContent="between" alignItems="center" className="mb-3">
            <Title className="text-purple-900">Churned Accounts</Title>
            <button onClick={() => setChurnExpanded(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
          </Flex>
          <Text className="text-slate-600 mb-3">
            HubSpot company-level churn flag. {arr.churned_account_count} accounts representing <strong>{fmtK(arr.churned_account_arr)}</strong> in original deal value have been marked churned.
          </Text>
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded border border-purple-100">
              <thead className="bg-purple-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs uppercase font-bold text-purple-900">Account</th>
                  <th className="px-3 py-2 text-left text-xs uppercase font-bold text-purple-900">Churn Date</th>
                  <th className="px-3 py-2 text-left text-xs uppercase font-bold text-purple-900">Reason</th>
                  <th className="px-3 py-2 text-right text-xs uppercase font-bold text-purple-900">Original $</th>
                </tr>
              </thead>
              <tbody>
                {arr.churned_accounts.map((acc, i) => (
                  <tr key={i} className="border-b border-purple-100 last:border-b-0">
                    <td className="px-3 py-2 font-bold text-slate-900">
                      {acc.name}
                      {acc.deal_name && acc.deal_name !== acc.name && (
                        <div className="text-xs text-slate-500 font-normal mt-0.5">deal: {acc.deal_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{acc.churn_date || '-'}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {acc.churn_reason || <span className="italic text-slate-400">no reason set</span>}
                      {acc.churn_reason_detailed && acc.churn_reason_detailed !== acc.churn_reason && (
                        <div className="text-[11px] italic text-slate-500 mt-0.5">"{acc.churn_reason_detailed}"</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtK(acc.deal_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {arr.churned_accounts.some(a => !a.churn_reason) && (
            <Text className="text-xs text-slate-500 mt-2">
              Empty churn reasons need to be filled in HubSpot company records by whoever owns CS. Once set, they appear here automatically.
            </Text>
          )}
        </Card>
      )}

      {/* Stripe billing (what we're actually collecting) */}
      <div className="mb-2">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Stripe Billing (currently active)</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
        <KPICard
          label="Active ARR"
          value={loadingArr ? '...' : fmtK(arr?.arr)}
          subtitle={loadingArr ? '' : `${arr?.active_customers || 0} active subscriptions`}
          icon={ArrowTrendingUpIcon}
          color="emerald"
        />
        <KPICard
          label="MRR"
          value={loadingArr ? '...' : fmtK(arr?.mrr)}
          subtitle={loadingArr ? '' : (arr?.past_due_customers > 0 ? `${arr.past_due_customers} past_due · ${fmtK(arr.past_due_arr)} ARR at risk` : 'all customers current')}
          icon={BanknotesIcon}
          color="indigo"
        />
        <KPICard
          label="Lifetime Revenue Collected"
          value={loadingArr ? '...' : fmtK(arr?.total_collected_lifetime)}
          subtitle="paid Stripe invoices (incl. wire/ACH)"
          icon={CurrencyDollarIcon}
          color="blue"
        />
      </div>

      {/* Bookings vs Billing gap */}
      {!loadingArr && arr && (
        <div className="mb-4 px-4 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
          <strong>Bookings vs Billing gap:</strong> HubSpot ARR <span className="font-bold">{fmtK(arr.hs_won_arr)}</span> vs Stripe Active ARR <span className="font-bold">{fmtK(arr.arr)}</span> = <span className="font-bold">{fmtK(arr.arr_gap)}</span> not currently being collected (churn, never billed, or unmatched). Click below to reconcile.
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm mb-4">
          Stripe error: {error}
        </div>
      )}

      {/* Collapsible reconciliation */}
      <Card className="overflow-visible">
        <button
          onClick={() => setReconExpanded(!reconExpanded)}
          className="w-full flex items-center justify-between text-left hover:bg-slate-50 -m-6 p-6 rounded transition-colors"
        >
          <div>
            <Title>HubSpot × Stripe Reconciliation</Title>
            <Text className="text-slate-500 mt-1">
              Won deals matched to actual collection · click to expand
            </Text>
          </div>
          <Flex justifyContent="end" alignItems="center" className="gap-3 max-w-xs">
            {recon && (
              <>
                <Badge color="red" size="xs">{recon.summary.invoiced_never_paid} never paid</Badge>
                <Badge color="amber" size="xs">{recon.summary.partial_payment} unpaid invoices</Badge>
              </>
            )}
            <span className={`text-slate-400 text-xl transition-transform ${reconExpanded ? 'rotate-90' : ''}`}>▶</span>
          </Flex>
        </button>

        {reconExpanded && (
          <div className="mt-4">
            {loadingRecon && <Text className="text-slate-400">Loading reconciliation (this can take 30-60s on first load)...</Text>}
            {recon && <ReconcileTable recon={recon} expandedRow={expandedRow} setExpandedRow={setExpandedRow} fmt={fmt} fmtK={fmtK} />}
          </div>
        )}
      </Card>
    </div>
  );
};

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-600', hoverRing: 'hover:ring-emerald-300', activeRing: 'ring-emerald-400' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',    icon: 'text-blue-600',    hoverRing: 'hover:ring-blue-300',    activeRing: 'ring-blue-400' },
  indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700',  icon: 'text-indigo-600',  hoverRing: 'hover:ring-indigo-300',  activeRing: 'ring-indigo-400' },
  slate:   { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700',   icon: 'text-slate-600',   hoverRing: 'hover:ring-slate-300',   activeRing: 'ring-slate-400' },
  purple:  { bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-700',  icon: 'text-purple-600',  hoverRing: 'hover:ring-purple-300',  activeRing: 'ring-purple-400' },
};

const KPICard = ({ label, value, subtitle, icon: Icon, color, onClick, expanded }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.blue;
  const clickable = typeof onClick === 'function';
  const cls = `${c.bg} ${c.border} ${clickable ? `cursor-pointer hover:ring-2 hover:ring-offset-1 ${c.hoverRing} transition` : ''} ${expanded ? `ring-2 ${c.activeRing}` : ''}`;
  return (
    <Card className={cls} onClick={onClick}>
      <Flex justifyContent="between" alignItems="start">
        <div className="flex-1">
          <Text className="text-xs uppercase tracking-wide font-bold text-slate-600">{label}</Text>
          <div className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{value}</div>
          {subtitle && <Text className="text-xs text-slate-500 mt-1">{subtitle}</Text>}
        </div>
        <div className={`${c.icon} bg-white rounded-full p-2`}>
          <Icon className="w-5 h-5" />
        </div>
      </Flex>
    </Card>
  );
};

const FLAG_BADGE = {
  OK: { color: 'emerald', label: 'OK' },
  INVOICED_NEVER_PAID: { color: 'red', label: 'NEVER PAID' },
  PARTIAL_PAYMENT: { color: 'amber', label: 'UNPAID INVOICES' },
  NO_STRIPE: { color: 'gray', label: 'NO STRIPE' },
  NO_INVOICES: { color: 'gray', label: 'NO INVOICES' },
  CHURNED: { color: 'purple', label: 'CHURNED' },
};

const ReconcileTable = ({ recon, expandedRow, setExpandedRow, fmt, fmtK }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [excludeChurned, setExcludeChurned] = React.useState(false);

  // Filter rows + recompute summary when "exclude churned" is on
  const visibleRows = excludeChurned ? recon.rows.filter(r => r.flag !== 'CHURNED') : recon.rows;
  const filteredSummary = excludeChurned ? {
    total_hs_amount: visibleRows.reduce((s, r) => s + (Number(r.hs_amount) || 0), 0),
    total_paid: visibleRows.reduce((s, r) => s + (Number(r.paid) || 0), 0),
    total_unpaid: visibleRows.reduce((s, r) => s + (Number(r.unpaid) || 0), 0),
    churned: 0,
    no_stripe_match: visibleRows.filter(r => r.flag === 'NO_STRIPE_MATCH').length,
  } : recon.summary;

  return (
    <div>
      {/* Filter toggle */}
      <div className="flex justify-end mb-3">
        <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-200 cursor-pointer hover:bg-gray-50 transition text-sm select-none">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
            checked={excludeChurned}
            onChange={e => setExcludeChurned(e.target.checked)}
          />
          <span className="text-gray-700 font-medium">Exclude churned</span>
          {excludeChurned && (
            <span className="text-xs text-gray-500 font-mono">
              ({recon.rows.length - visibleRows.length} hidden)
            </span>
          )}
        </label>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <SummaryPill label={excludeChurned ? "HS Active $" : "HS Lifetime $"} value={fmtK(filteredSummary.total_hs_amount)} color="bg-slate-100 text-slate-900" />
        <SummaryPill label="Stripe Collected" value={fmtK(filteredSummary.total_paid)} color="bg-emerald-100 text-emerald-900" />
        <SummaryPill label="Issued, Unpaid" value={fmtK(filteredSummary.total_unpaid)} color="bg-amber-100 text-amber-900" />
        <SummaryPill label="Churned" value={filteredSummary.churned ?? 0} color="bg-purple-100 text-purple-900" />
        <SummaryPill label="No Stripe Match" value={filteredSummary.no_stripe_match} color="bg-gray-100 text-gray-900" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 border-b border-slate-200">
            <tr>
              <th className="w-6"></th>
              <th className="px-3 py-2 text-left text-xs uppercase font-bold text-slate-600">Deal</th>
              <th className="px-3 py-2 text-left text-xs uppercase font-bold text-slate-600 hidden md:table-cell">Closed</th>
              <th className="px-3 py-2 text-right text-xs uppercase font-bold text-slate-600">HS Amt</th>
              <th className="px-3 py-2 text-left text-xs uppercase font-bold text-slate-600 hidden lg:table-cell">Stripe Customer</th>
              <th className="px-3 py-2 text-left text-xs uppercase font-bold text-slate-600 hidden xl:table-cell">Sub Status</th>
              <th className="px-3 py-2 text-right text-xs uppercase font-bold text-slate-600">Paid</th>
              <th className="px-3 py-2 text-right text-xs uppercase font-bold text-slate-600">Unpaid</th>
              <th className="px-3 py-2 text-left text-xs uppercase font-bold text-slate-600">Flag</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, idx) => {
              const isExpanded = expandedRow === idx;
              const flag = FLAG_BADGE[r.flag] || { color: 'gray', label: r.flag };
              return (
                <React.Fragment key={`${r.deal_id}-${idx}`}>
                  <tr
                    className="border-b border-slate-100 cursor-pointer hover:bg-blue-50"
                    onClick={() => setExpandedRow(isExpanded ? null : idx)}
                  >
                    <td className="text-slate-400 px-2"><span className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span></td>
                    <td className="px-3 py-2 font-bold text-slate-900">
                      {r.deal_name}
                      {r.is_churned && (
                        <div className="text-xs text-purple-700 font-normal mt-0.5">
                          Churned{r.churn_date ? ` ${r.churn_date}` : ''}{r.churn_reason ? ` · ${r.churn_reason}` : ''}
                          {r.churn_reason_detailed && r.churn_reason_detailed !== r.churn_reason && (
                            <div className="text-[11px] text-purple-600 italic mt-0.5">"{r.churn_reason_detailed}"</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 hidden md:table-cell">{r.deal_close_date}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtK(r.deal_amount)}</td>
                    <td className="px-3 py-2 text-xs hidden lg:table-cell">{r.stripe_customer || '-'}</td>
                    <td className="px-3 py-2 text-xs hidden xl:table-cell"><code className="text-[10px]">{r.sub_status}</code></td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.total_paid > 0 ? fmtK(r.total_paid) : '-'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.total_unpaid > 0 ? <strong className="text-red-600">{fmtK(r.total_unpaid)}</strong> : '-'}
                      {r.unpaid_invoices?.length > 0 && (
                        <div className="text-xs text-red-600 mt-0.5 font-normal">
                          {r.unpaid_invoices.slice(0, 3).map(inv => (
                            <div key={inv.invoice_number}>
                              {inv.issued_date}: {fmtK(inv.amount)}{inv.due_date ? ` (due ${inv.due_date})` : ''}{inv.due_date && inv.due_date < today ? ' OVERDUE' : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2"><Badge color={flag.color} size="xs">{flag.label}</Badge></td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-50">
                      <td colSpan={9} className="px-6 py-4">
                        <div className="text-xs font-bold text-slate-700 mb-2">All invoices for {r.stripe_customer || r.deal_name}</div>
                        {r.all_invoices?.length === 0 ? (
                          <Text className="text-slate-400">No invoices on file in Stripe.</Text>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="px-2 py-1 text-left">Invoice #</th>
                                  <th className="px-2 py-1 text-left">Issued</th>
                                  <th className="px-2 py-1 text-left">Due</th>
                                  <th className="px-2 py-1 text-left">Paid On</th>
                                  <th className="px-2 py-1 text-right">Total</th>
                                  <th className="px-2 py-1 text-right">Amt Due</th>
                                  <th className="px-2 py-1 text-left">Status</th>
                                  <th className="px-2 py-1"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.all_invoices.map(inv => (
                                  <tr key={inv.invoice_id} className="border-b border-slate-200">
                                    <td className="px-2 py-1 font-mono text-[10px]">{inv.invoice_number}</td>
                                    <td className="px-2 py-1">{inv.issued_date}</td>
                                    <td className="px-2 py-1">{inv.due_date || '-'}</td>
                                    <td className="px-2 py-1">{inv.paid_at || '-'}</td>
                                    <td className="px-2 py-1 text-right tabular-nums">{fmt(inv.total)}</td>
                                    <td className="px-2 py-1 text-right tabular-nums">{inv.amount_due > 0 ? <strong className="text-red-600">{fmt(inv.amount_due)}</strong> : '-'}</td>
                                    <td className="px-2 py-1"><InvoiceStatusBadge status={inv.status} /></td>
                                    <td className="px-2 py-1">
                                      {inv.hosted_invoice_url && (
                                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-[10px]">View</a>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SummaryPill = ({ label, value, color }) => (
  <div className={`${color} rounded-md px-3 py-2`}>
    <div className="text-[10px] uppercase tracking-wide font-bold opacity-70">{label}</div>
    <div className="text-lg font-bold tabular-nums">{value}</div>
  </div>
);

const STATUS_COLOR = {
  paid: 'emerald', open: 'red', uncollectible: 'red', void: 'gray', draft: 'blue',
};
const InvoiceStatusBadge = ({ status }) => (
  <Badge color={STATUS_COLOR[status] || 'gray'} size="xs">{status?.toUpperCase()}</Badge>
);

export default RevenueKPIs;
